import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	inject,
	signal,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Subject } from "rxjs";

import { FeatureService } from "app/shared/services/feature.service";

import { DynamicFormBuilder } from "app/shared/modules/ngx-dynamic-forms";
import { DarBriefingHelper } from "app/shared";

import * as interpolationParser from "../../../../shared/modules/ngx-dynamic-forms/utils/interpolation-parser";
import { TaskFormResult } from "../../../types/task-form-result";
import { tasksUpdateValidator } from "./tasks-update-form.validator";
import { Task } from "app/shared/types";
import { DynamicFormConfig, DFormControl } from "../../../../shared/modules/ngx-dynamic-forms/entities";
import { map, startWith, takeUntil, tap } from "rxjs/operators";
import { FormFieldFactory } from "app/shared/modules/ngx-dynamic-forms/services";

@Component({
	selector: "ic-tasks-update-form",
	templateUrl: "tasks-update-form.component.html",
	styleUrls: ["tasks-update-form.component.scss"],
	providers: [DarBriefingHelper],
	standalone: false,
})
export class TasksUpdateFormComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
	private readonly formBuilder = inject(DynamicFormBuilder);
	private readonly formFieldFactory = inject(FormFieldFactory);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly darBriefingHelper = inject(DarBriefingHelper);

	protected readonly hasValidationByHeadNurse = inject(FeatureService).hasEveryFeature(["ValidationByHeadNurse"]);

	// @ts-expect-error TS2564
	@Input() task: Task;
	// @ts-expect-error TS2564
	@Input() formConfig: DynamicFormConfig;
	// @ts-expect-error TS2564
	@Input() disabled: boolean;

	@Output() create = new EventEmitter<TaskFormResult>();

	// @ts-expect-error TS2564
	protected form: UntypedFormGroup;

	/**
	 * Reactive signal tracking form submission readiness. Subscribes to `form.statusChanges` so that async validity
	 * updates (e.g. from DynamicFormComponent disabling conditional controls) are properly picked up by Angular's change
	 * detection — same pattern as WoundCareUpdateComponent.formValid.
	 */
	protected formCanSubmit = signal(false);

	private readonly destroy$ = new Subject<void>();
	private readonly formRebuild$ = new Subject<void>();
	private lastFormConfigJson: string | null = null;

	ngOnInit() {
		if (this.disabled && this.form) {
			this.form.disable();
		}
	}

	save(form: UntypedFormGroup) {
		this.create.emit(form.value);
	}

	setupForm() {
		if (!this.formConfig || !this.task) {
			return;
		}

		// Tear down previous subscription bound to the old form instance
		this.formRebuild$.next();

		let evaluationFormValue;

		if (this.formConfig && this.formConfig.length > 0) {
			const evaluationFormControl = this.formFieldFactory.getCustomControl(this.formConfig[0] as DFormControl);

			if (evaluationFormControl) {
				/**
				 * The form was broken whenever a `moment` toggle was present. This issue was fixed by:
				 * https://github.com/intocare/medapp/pull/305, but introduced a new issue: Whenever the form wasn't initialized
				 * with a `moment` toggle it was broken, hence the if statement.
				 */
				if (evaluationFormControl.definition.some((definition) => definition.name === "moment")) {
					const output = evaluationFormControl["@output"];
					if (typeof output === "object" && output) {
						evaluationFormValue = interpolationParser.interpolate(
							output,
							/**
							 * This works because for now all the `custom-form-configs` only have one definition if `moment` is
							 * present. However, whenever an extra definition is added this will break indicating how fragile this
							 * code is.
							 */
							(evaluationFormControl.definition[0] as DFormControl).value,
							true,
						);
					}
				}
			}
		}

		const evaluationForm = this.formBuilder.dynamicGroup(this.formConfig);

		this.form = this.formBuilder.group(
			{
				important: [],
				initializeDar: [],
				validationRequired: [],
				executed: [],
				evaluationForm,
				notes: [],
				darActions: [],
			},
			{ validator: tasksUpdateValidator },
		);

		const formValues = {
			important: this.task.important || false,
			initializeDar: this.task.initializeDar || false,
			validationRequired: this.task.validationRequired || false,
			executed: this.task.completed === false ? true : this.task.executed,
			evaluationForm:
				this.task.evaluationForm
				|| (evaluationFormValue
					? {
							[this.formConfig[0]["name"]]: evaluationFormValue,
						}
					: {}),
			notes: this.task.notes || "",
			darActions: this.task.darActions ? this.task.darActions : [],
		};

		this.form.patchValue(formValues);

		this.darBriefingHelper.handleInitializeDarBriefingSlider(this.form);

		if (this.task.completed || !this.task.hasUserHandlePermission || this.disabled) {
			this.form.disable();
		}

		// Subscribe to the "executed" control so the evaluationForm
		// sub-group is added/removed in sync with the current form instance.
		this.form
			.get("executed")!
			.valueChanges.pipe(
				tap((isExecuted) => {
					if (isExecuted) {
						const form = this.formBuilder.dynamicGroup(this.formConfig);
						this.form.addControl("evaluationForm", form);
						return;
					}

					this.form.removeControl("evaluationForm");
				}),
				takeUntil(this.formRebuild$),
				takeUntil(this.destroy$),
			)
			.subscribe();

		// Track form validity reactively via statusChanges so the submit button
		// updates after async control enable/disable in DynamicFormComponent.
		this.form.statusChanges
			.pipe(
				startWith(this.form.status),
				map(() => this.form.valid && this.form.enabled),
				takeUntil(this.formRebuild$),
				takeUntil(this.destroy$),
			)
			.subscribe((canSubmit) => this.formCanSubmit.set(canSubmit));
	}

	ngAfterViewInit() {
		this.cdRef.detectChanges();
	}

	ngOnChanges(changes: SimpleChanges) {
		const taskChanged = changes.task && changes.task.previousValue?.completed !== changes.task.currentValue.completed;

		// Compare formConfig by content to avoid rebuilding the form when
		// Array.filter() in getEvaluationForm() produces a new reference
		// with identical content on every store emission.
		let formConfigContentChanged = false;
		if (changes.formConfig) {
			const newJson = JSON.stringify(changes.formConfig.currentValue);
			if (newJson !== this.lastFormConfigJson) {
				this.lastFormConfigJson = newJson;
				formConfigContentChanged = true;
			}
		}

		if (taskChanged || formConfigContentChanged) {
			this.setupForm();
		}
	}

	ngOnDestroy() {
		this.formRebuild$.next();
		this.formRebuild$.complete();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
